import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTemplateImages } from 'hooks';
import { Tabs, TabList, TabPanels, TabPanel, Tab } from '@reach/tabs';
import { FormattedMessage } from 'react-intl';
import { getSelectedImageUrl } from 'helpers';
import {
  AiImages,
  GlobalIconImages,
  GlobalImages,
  // IconImages, // TODO: uncomment when Iconfinder tab is re-enabled
  TemplateIconImages,
  TemplateImages,
  UnsplashImages,
} from './components';
import './ImageModal.sass';

function ImageModal({ withIconsTab, onSetImageUrl, id, editor }) {
  const [tabIndex, setTabIndex] = useState(0);

  const selected = editor?.getSelected();
  const wrapper = editor?.getWrapper();

  const {
    templateImagesState: {
      data: templateImagesData,
      loading: templateImagesLoading,
    },
    templateIconsState: {
      data: templateIconsData,
      loading: templateIconsLoading,
    },
    isConfirmTemplateDeleteModalOpen,
    openConfirmTemplateDeleteModal,
    closeConfirmTemplateDeleteModal,
    isTemplateImageDeleteLoading,
    handleDeleteImage,
    globalImagesState: { data: globalImagesData, loading: globalImagesLoading },
    isConfirmGlobalDeleteModalOpen,
    openConfirmGlobalDeleteModal,
    closeConfirmGlobalDeleteModal,
    isGlobalImageDeleteLoading,
    isUploadFolderModalOpen,
    isUploadLoading,
    handleUploadImage,
    handleUploadWithFolder,
    handleCloseUploadFolderModal,
    handleDeleteGlobalImage,
  } = useTemplateImages(id, withIconsTab);

  // If the user deletes the last template icon while the Template Icons tab is
  // active, fall back to the Global Icons tab so the UI stays consistent.
  useEffect(() => {
    if (withIconsTab && tabIndex === 1 && !templateIconsData?.length) {
      setTabIndex(0);
    }
  }, [withIconsTab, tabIndex, templateIconsData]);

  const handleSetImageUrl = (url, imageName) => {
    onSetImageUrl({ url, imageName, isIcon: withIconsTab });
  };

  // Remove the thumbnail when a user has not selected any image/icon
  useEffect(() => {
    return () => {
      if (selected) {
        const { 'data-type': type, src } = selected.getAttributes();

        if (
          (type === 'image' || type === 'icon-wrapper') &&
          src?.startsWith('data:image/svg+xml;base64,CjxzdmcgeG1sbnM9')
        ) {
          selected.remove();
          editor.select(wrapper);
        }
      }
    };
  }, [selected, wrapper, editor]);

  return (
    <div className="image-modal">
      {withIconsTab ? (
        <Tabs index={tabIndex} onChange={setTabIndex}>
          <TabList>
            <Tab as="div">
              <FormattedMessage id="image-modal.global-icons.tab" />
            </Tab>
            <Tab as="div" disabled={templateIconsData?.length === 0}>
              <FormattedMessage id="image-modal.template-icons.tab" />
            </Tab>
            {/* TODO: uncomment this tab when Iconfinder search is re-enabled
            <Tab as="div">
              <FormattedMessage id="image-modal.icon-images.tab" />
            </Tab>
            */}
          </TabList>
          <TabPanels>
            <TabPanel>
              <GlobalIconImages
                id={id}
                selectedImageUrl={getSelectedImageUrl(selected)}
                onSetImageUrl={handleSetImageUrl}
                data={globalImagesData}
                loading={globalImagesLoading}
                isConfirmGlobalDeleteModalOpen={isConfirmGlobalDeleteModalOpen}
                openConfirmGlobalDeleteModal={openConfirmGlobalDeleteModal}
                closeConfirmGlobalDeleteModal={closeConfirmGlobalDeleteModal}
                isGlobalImageDeleteLoading={isGlobalImageDeleteLoading}
                isUploadFolderModalOpen={isUploadFolderModalOpen}
                isUploadLoading={isUploadLoading}
                handleUploadImage={handleUploadImage}
                handleUploadWithFolder={handleUploadWithFolder}
                handleCloseUploadFolderModal={handleCloseUploadFolderModal}
                handleDeleteGlobalImage={handleDeleteGlobalImage}
              />
            </TabPanel>
            <TabPanel>
              <TemplateIconImages
                selectedImageUrl={getSelectedImageUrl(selected)}
                id={id}
                onSetImageUrl={handleSetImageUrl}
                editor={editor}
                data={templateIconsData}
                loading={templateIconsLoading}
                isConfirmTemplateDeleteModalOpen={
                  isConfirmTemplateDeleteModalOpen
                }
                openConfirmTemplateDeleteModal={openConfirmTemplateDeleteModal}
                closeConfirmTemplateDeleteModal={
                  closeConfirmTemplateDeleteModal
                }
                isTemplateImageDeleteLoading={isTemplateImageDeleteLoading}
                handleDeleteImage={handleDeleteImage}
              />
            </TabPanel>
            {/* TODO: uncomment this panel when Iconfinder search is re-enabled
            <TabPanel>
              <IconImages
                isActive={tabIndex === 2}
                onSetImageUrl={handleSetImageUrl}
              />
            </TabPanel>
            */}
          </TabPanels>
        </Tabs>
      ) : (
        <Tabs>
          <TabList>
            <>
              <Tab as="div">
                <FormattedMessage id="image-modal.global-images.tab" />
              </Tab>
              <Tab as="div" disabled={templateImagesData?.length === 0}>
                <FormattedMessage id="image-modal.template-images.tab" />
              </Tab>
              <Tab as="div">
                <FormattedMessage id="image-modal.free-images.tab" />
              </Tab>
              <Tab style={{ color: '#2684FF' }} as="div">
                <span className="icon icon-magic-wand" />
                <FormattedMessage id="image-modal.ai-images.tab" />
              </Tab>
            </>
          </TabList>
          <TabPanels>
            <>
              <TabPanel>
                <GlobalImages
                  selectedImageUrl={getSelectedImageUrl(selected)}
                  id={id}
                  onSetImageUrl={handleSetImageUrl}
                  data={globalImagesData}
                  loading={globalImagesLoading}
                  isConfirmGlobalDeleteModalOpen={
                    isConfirmGlobalDeleteModalOpen
                  }
                  openConfirmGlobalDeleteModal={openConfirmGlobalDeleteModal}
                  closeConfirmGlobalDeleteModal={closeConfirmGlobalDeleteModal}
                  isGlobalImageDeleteLoading={isGlobalImageDeleteLoading}
                  isUploadFolderModalOpen={isUploadFolderModalOpen}
                  isUploadLoading={isUploadLoading}
                  handleUploadImage={handleUploadImage}
                  handleUploadWithFolder={handleUploadWithFolder}
                  handleCloseUploadFolderModal={handleCloseUploadFolderModal}
                  handleDeleteGlobalImage={handleDeleteGlobalImage}
                />
              </TabPanel>
              <TabPanel>
                <TemplateImages
                  selectedImageUrl={getSelectedImageUrl(selected)}
                  id={id}
                  onSetImageUrl={handleSetImageUrl}
                  editor={editor}
                  data={templateImagesData}
                  loading={templateImagesLoading}
                  isConfirmTemplateDeleteModalOpen={
                    isConfirmTemplateDeleteModalOpen
                  }
                  openConfirmTemplateDeleteModal={
                    openConfirmTemplateDeleteModal
                  }
                  closeConfirmTemplateDeleteModal={
                    closeConfirmTemplateDeleteModal
                  }
                  isTemplateImageDeleteLoading={isTemplateImageDeleteLoading}
                  handleDeleteImage={handleDeleteImage}
                />
              </TabPanel>
              <TabPanel>
                <UnsplashImages onSetImageUrl={handleSetImageUrl} />
              </TabPanel>
              <TabPanel>
                <AiImages onSetImageUrl={handleSetImageUrl} />
              </TabPanel>
            </>
          </TabPanels>
        </Tabs>
      )}
    </div>
  );
}

ImageModal.propTypes = {
  editor: PropTypes.object,
  withIconsTab: PropTypes.bool,
  onSetImageUrl: PropTypes.func.isRequired,
  id: PropTypes.string.isRequired,
};

export default ImageModal;
